/**
 * Owner-visible phase of a project command, derived only from the command and
 * its plans. Pure - no network, no clock. Reload/reconnect re-runs the same
 * function over the same rows and must yield the same terminal phase.
 *
 * QUEUED is only honest while no plan has landed yet. A terminal no-work plan
 * (answered question, failed draft with no tasks, or accepted/rejected empty
 * plan) must never read as still waiting on Worker/model work.
 */
import type { OwnerCommand, Plan } from './data';
import { wasAnswered } from './plans';

export type OwnerCommandPhase =
  | 'queued'
  | 'answered'
  | 'failed'
  | 'no_work'
  | 'duplicate'
  | 'awaiting_answer'
  | 'awaiting_approval'
  | 'approved'
  | 'rejected';

/** Holds that need a conversational correction, not an approval decision. */
export const TARGET_ANSWER_REASONS = new Set([
  'worker_target_conflict', 'worker_retired', 'not_found', 'ambiguous_worker_target',
  'ambiguous_runtime_target', 'machine_mismatch', 'runtime_not_on_machine', 'no_eligible_worker',
  'ambiguous_scope',
]);

/** Fallback Brunel reply when an answered question row carries no summary. */
export const ANSWERED_NO_WORK_TEXT = 'Answered — no work created.';

/** Fallback when an approved/rejected plan lists no tasks. */
export const NO_WORK_CREATED_TEXT = 'No work created.';

/**
 * The newest plan for a command, by created_at. Ties prefer the later row in
 * the input (stable enough for tests that stamp equal times).
 */
export function latestPlanForCommand(
  commandId: string,
  plans: readonly Plan[],
): Plan | null {
  let latest: Plan | null = null;
  let latestMs = -Infinity;
  for (const plan of plans) {
    if (plan.command_id !== commandId) continue;
    const parsed = Date.parse(plan.created_at);
    const atMs = Number.isNaN(parsed) ? 0 : parsed;
    if (latest === null || atMs >= latestMs) {
      latest = plan;
      latestMs = atMs;
    }
  }
  return latest;
}

function taskCount(plan: Plan): number {
  return Array.isArray(plan.tasks) ? plan.tasks.length : 0;
}

/**
 * Owner-visible phase for one command, from the plans that belong to it.
 * Callers may pass every plan in the desk; only matching command_id rows count.
 */
export function ownerCommandPhase(
  command: OwnerCommand,
  plansForCommand: readonly Plan[],
): OwnerCommandPhase {
  const mine = plansForCommand.filter((p) => p.command_id === command.id);
  const plan = latestPlanForCommand(command.id, mine);
  if (plan === null) return 'queued';

  if (wasAnswered(plan)) return 'answered';

  const tasks = taskCount(plan);
  if (plan.status === 'failed') return 'failed';

  if ((plan.status === 'approved' || plan.status === 'rejected') && tasks === 0) {
    return 'no_work';
  }

  if (plan.status === 'proposed') {
    if (plan.held_for === 'duplicate_command') return 'duplicate';
    return plan.held_for !== null && plan.held_for !== undefined && TARGET_ANSWER_REASONS.has(plan.held_for)
      ? 'awaiting_answer'
      : 'awaiting_approval';
  }
  if (plan.status === 'approved') return 'approved';
  if (plan.status === 'rejected') return 'rejected';

  return 'queued';
}

/** Exact owner-facing label for a phase. */
export function ownerCommandStatusLabel(phase: OwnerCommandPhase): string {
  switch (phase) {
    case 'queued': return 'QUEUED';
    case 'answered': return 'ANSWERED';
    case 'no_work': return 'NO WORK CREATED';
    case 'failed': return 'FAILED';
    case 'duplicate': return 'ALREADY RUNNING';
    case 'awaiting_answer': return 'NEEDS YOUR ANSWER';
    case 'awaiting_approval': return 'waiting on you';
    case 'approved': return 'approved';
    case 'rejected': return 'dismissed';
  }
}
